import React, { useEffect, useMemo, useState } from "react";
import { authFetch } from "../../utils/authFetch";

const SpouseSelector = ({ recordType, record, onLinked, onUnlinked }) => {
  const recordId = record?.id;
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [linkedSpouseName, setLinkedSpouseName] = useState("");

  const spouseRecordType = record?.spouse_record_type || "";
  const spouseRecordId = record?.spouse_record_id || "";
  const spouseName = record?.spouse_name || linkedSpouseName || "";

  const hasLinkedSpouse = Boolean(spouseRecordType && spouseRecordId);

  const spouseProfilePath = useMemo(() => {
    if (!hasLinkedSpouse) return "";

    if (spouseRecordType === "client") {
      return `/client-profile/${spouseRecordId}`;
    }

    if (spouseRecordType === "lead") {
      return `/lead-profile/${spouseRecordId}`;
    }

    return "";
  }, [hasLinkedSpouse, spouseRecordType, spouseRecordId]);

  useEffect(() => {
    const loadLinkedSpouseName = async () => {
      if (!hasLinkedSpouse || !spouseRecordId || !spouseRecordType) {
        setLinkedSpouseName("");
        return;
      }

      if (record?.spouse_name) {
        setLinkedSpouseName(record.spouse_name);
        return;
      }

      try {
        const endpoint =
          spouseRecordType === "client"
            ? `/api/clients/${spouseRecordId}`
            : `/api/leads/${spouseRecordId}`;

        const res = await authFetch(endpoint);
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data.error || "Failed to load linked spouse.");
        }

        const spouseRecord = data.client || data.lead || data;
        const displayName = `${spouseRecord.first_name || ""} ${spouseRecord.last_name || ""}`.trim();

        setLinkedSpouseName(displayName || "");
      } catch (err) {
        console.error("Load linked spouse error:", err);
        setLinkedSpouseName("");
      }
    };

    loadLinkedSpouseName();
  }, [hasLinkedSpouse, spouseRecordType, spouseRecordId, record?.spouse_name]);

  useEffect(() => {
    const timeout = setTimeout(async () => {
      const cleanQuery = query.trim();

      if (cleanQuery.length < 2 || !recordId) {
        setResults([]);
        return;
      }

      try {
        setSearching(true);

        const res = await authFetch(
          `/api/spouses/search?q=${encodeURIComponent(cleanQuery)}&excludeType=${recordType}&excludeId=${recordId}`
        );

        const data = await res.json().catch(() => []);

        if (!res.ok) {
          throw new Error(data.error || "Failed to search spouse records.");
        }

        setResults(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Spouse search error:", err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [query, recordType, recordId]);

  const handleLink = async (result) => {
    try {
      setSaving(true);
      setMessage("");

      const res = await authFetch("/api/spouses/link", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recordType,
          recordId,
          spouseRecordType: result.recordType,
          spouseRecordId: result.id
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || "Failed to link spouse.");
      }

      setQuery("");
      setResults([]);
      setLinkedSpouseName(result.name || "");
      setMessage("");

      if (onLinked) {
        onLinked({
          spouse_record_type: result.recordType,
          spouse_record_id: result.id,
          spouse_name: result.name
        });
      }
    } catch (err) {
      console.error("Spouse link error:", err);
      setMessage(err.message || "Failed to link spouse.");
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async () => {
    if (!window.confirm("Unlink this spouse relationship?")) return;

    try {
      setSaving(true);
      setMessage("");

      const res = await authFetch("/api/spouses/unlink", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recordType,
          recordId
        })
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data.error || "Failed to unlink spouse.");
      }

      setQuery("");
      setResults([]);
      setLinkedSpouseName("");
      setMessage("");

      if (onUnlinked) {
        onUnlinked();
      }
    } catch (err) {
      console.error("Spouse unlink error:", err);
      setMessage(err.message || "Failed to unlink spouse.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="spouse-selector">
      {hasLinkedSpouse ? (
        <div className="spouse-linked-card">
          <div className="spouse-linked-name">
            {spouseName || "Linked spouse"}
          </div>

          <div className="spouse-linked-actions">
            {spouseProfilePath && (
              <a className="sales-secondary-btn" href={spouseProfilePath}>
                View
              </a>
            )}

            <button
              type="button"
              className="sales-secondary-btn"
              onClick={handleUnlink}
              disabled={saving}
            >
              Unlink
            </button>
          </div>
        </div>
      ) : (
        <>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search spouse..."
            disabled={saving}
          />

          {query.trim().length >= 2 && (
            <div className="spouse-search-results">
              {searching ? (
                <div className="spouse-search-empty">Searching...</div>
              ) : results.length > 0 ? (
                results.map((result) => (
                  <button
                    key={`${result.recordType}-${result.id}`}
                    type="button"
                    className="spouse-search-result"
                    onClick={() => handleLink(result)}
                    disabled={saving}
                  >
                    <span>
                      <strong>{result.name}</strong>
                      <small>{result.recordType === "client" ? "Client" : "Lead"}</small>
                    </span>

                    <span className="spouse-search-contact">
                      {result.phone || result.email || ""}
                    </span>
                  </button>
                ))
              ) : (
                <div className="spouse-search-empty">No matching records found.</div>
              )}
            </div>
          )}
        </>
      )}

      {message && (
        <div className="spouse-selector-message">
          {message}
        </div>
      )}
    </div>
  );
};

export default SpouseSelector;